import type { GridSpec } from '../sim/grid.ts';

/**
 * Crown cover from NIBIO's SR16 forest resource map (16 m), via its WMS — CORS-enabled, so the
 * browser fetches it directly. There's no WCS, so the layer comes as a rendered PNG in ten
 * colour classes (0–10 % … 90–100 %; the last two share a colour) and is decoded back to cover
 * by matching the legend. Transparent = no forest.
 */

const SR16_WMS = 'https://wms.nibio.no/cgi-bin/sr16';
/** WMS image size cap (MapServer's default MAXSIZE is 4096). */
const MAX_PX = 2048;

/** Legend colours of layer SRRKRONEDEK, lowest class first, with each class's mid cover. */
const CROWN_CLASSES: { rgb: [number, number, number]; cover: number }[] = [
  { rgb: [229, 245, 224], cover: 0.05 },
  { rgb: [199, 233, 192], cover: 0.15 },
  { rgb: [161, 217, 155], cover: 0.25 },
  { rgb: [116, 196, 118], cover: 0.35 },
  { rgb: [65, 171, 93], cover: 0.45 },
  { rgb: [35, 139, 69], cover: 0.55 },
  { rgb: [0, 109, 44], cover: 0.65 },
  { rgb: [0, 68, 27], cover: 0.75 },
  // 80–90 % and 90–100 % are drawn in the same colour.
  { rgb: [0, 40, 15], cover: 0.85 },
];

/** Crown cover (0–1) per pixel of RGBA image data; nearest legend colour, 0 if transparent. */
export function decodeCrownCover(rgba: Uint8ClampedArray | Uint8Array): Float32Array {
  const n = rgba.length / 4;
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    if (rgba[4 * i + 3]! < 128) continue;
    const r = rgba[4 * i]!;
    const g = rgba[4 * i + 1]!;
    const b = rgba[4 * i + 2]!;
    let best = 0;
    let bestD = Infinity;
    for (const c of CROWN_CLASSES) {
      const d = (r - c.rgb[0]) ** 2 + (g - c.rgb[1]) ** 2 + (b - c.rgb[2]) ** 2;
      if (d < bestD) {
        bestD = d;
        best = c.cover;
      }
    }
    out[i] = best;
  }
  return out;
}

/** Nearest-neighbour resample of a w×h image field onto the grid's cols×rows. */
export function resampleNearest(
  src: Float32Array,
  w: number,
  h: number,
  cols: number,
  rows: number,
): Float32Array {
  if (w === cols && h === rows) return src;
  const out = new Float32Array(cols * rows);
  for (let r = 0; r < rows; r++) {
    const sr = Math.min(h - 1, Math.floor(((r + 0.5) * h) / rows));
    for (let c = 0; c < cols; c++) {
      const sc = Math.min(w - 1, Math.floor(((c + 0.5) * w) / cols));
      out[r * cols + c] = src[sr * w + sc]!;
    }
  }
  return out;
}

/** Crown cover (0–1) per cell of `spec` (EPSG:25833), fetched and decoded in the browser. */
export async function fetchCrownCover(spec: GridSpec, signal?: AbortSignal): Promise<Float32Array> {
  // Decoding needs a browser canvas (not available in Node, e.g. in tests).
  if (typeof createImageBitmap === 'undefined') throw new Error('SR16: needs a browser');
  const w = Math.min(spec.cols, MAX_PX);
  const h = Math.min(spec.rows, MAX_PX);
  const maxX = spec.minX + spec.cols * spec.cellSize;
  const minY = spec.maxY - spec.rows * spec.cellSize;
  const params = new URLSearchParams({
    SERVICE: 'WMS',
    VERSION: '1.3.0',
    REQUEST: 'GetMap',
    LAYERS: 'SRRKRONEDEK',
    STYLES: '',
    CRS: 'EPSG:25833',
    BBOX: `${spec.minX},${minY},${maxX},${spec.maxY}`,
    WIDTH: String(w),
    HEIGHT: String(h),
    FORMAT: 'image/png',
    TRANSPARENT: 'TRUE',
  });
  const res = await fetch(`${SR16_WMS}?${params}`, signal ? { signal } : {});
  if (!res.ok) throw new Error(`SR16: HTTP ${res.status}`);
  const type = res.headers.get('content-type') ?? '';
  // WMS errors come back as XML with a 200.
  if (!type.startsWith('image/')) throw new Error('SR16: the map server returned an error');
  // No colour management: the pixel values are the data.
  const bitmap = await createImageBitmap(await res.blob(), {
    colorSpaceConversion: 'none',
    premultiplyAlpha: 'none',
  });
  const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('SR16: no 2D canvas');
  ctx.drawImage(bitmap, 0, 0);
  const img = ctx.getImageData(0, 0, bitmap.width, bitmap.height);
  const cover = decodeCrownCover(img.data);
  return resampleNearest(cover, bitmap.width, bitmap.height, spec.cols, spec.rows);
}
